import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../services/authentication/authContext";
import { signOut as supabaseSignOut } from "../../../../services/authentication/supabaseService";
import { useSubscription } from "../../../../hooks/useSubscription";
import LanguageSwitch from "./LanguageSwitch";
import { useBookDemo } from "../../../../hooks/useBookDemo";

export const Header: React.FC<{ homeAnchors?: boolean; compactLogo?: boolean }> = ({ homeAnchors = false, compactLogo = false }) => {
  const navigate = useNavigate();
  const openBookDemo = useBookDemo("header");
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isActive } = useSubscription();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  const navLinks = [
    { key: "product", label: t("landing.nav.product"), href: homeAnchors ? "/#hero" : "#hero" },
    { key: "features", label: t("landing.nav.features"), href: "/#features" },
    { key: "pricing", label: t("landing.nav.pricing"), href: "/#pricing" },
    { key: "contact", label: t("landing.nav.contact"), href: "/#contact" },
    { key: "careers", label: t("landing.nav.careers"), href: "/careers" },
  ];

  const toggleMenu = useCallback((): void => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const handleLogout = async () => {
    try {
      await supabaseSignOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error', err);
    }
  };

  useEffect((): (() => void) => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return (): void => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  useEffect((): (() => void) => {
    const onScroll = (): void => {
      setHasScrolled(window.scrollY > 8);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return (): void => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div
      className={`bg-landingPageBG sticky top-0 z-20 px-5 md:px-6 py-4 md:py-[22px] transition-shadow duration-200 ${
        hasScrolled ? "shadow-lg" : ""
      }`}
    >
      <div className="max-w-[1140px] mx-auto flex items-center justify-between gap-4">
        <Link to="/" onClick={() => sessionStorage.removeItem('hasRedirectedToPayment')}>
          <img src="/assets/logo.svg" alt="logo" width={180} height={32} className={compactLogo ? 'w-[150px] sm:w-[180px]' : undefined} />
        </Link>
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              className="text-white text-[18px] leading-6 font-medium"
            >
              {link.label}
            </a>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          <LanguageSwitch />
          {user ? (
            <>
              <button className="text-white text-[18px] leading-6 font-medium" onClick={() => navigate("/userProfile")}>
                {t("landing.nav.profile")}
              </button>
              <button className="text-white text-[18px] leading-6 font-medium" onClick={handleLogout}>
                {t("landing.nav.logout")}
              </button>
            </>
          ) : (
            <button className="text-white text-[18px] leading-6 font-medium" onClick={() => navigate("/login")}>
              {t("landing.nav.login")}
            </button>
          )}
          <button
            onClick={user ? (isActive ? () => navigate("/dashboard") : () => navigate("/payment")) : openBookDemo}
            className="bg-white h-11 rounded-lg px-4 text-primary text-[18px] leading-6 font-semibold shadow-[0px_0px_0px_1px_#01040B,_0px_1px_2px_0px_#01040B7A] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(15,23,42,0.2)] active:translate-y-0 active:shadow-[0px_0px_0px_1px_#01040B,_0px_1px_2px_0px_#01040B7A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-headerbg"
          >
            {user ? t("landing.nav.toDashboard") : t("landing.nav.getStarted")}
          </button>
        </div>
        <div className="flex items-center gap-4 lg:hidden">
          <LanguageSwitch mobile />
          <button
            onClick={toggleMenu}
            aria-label={t("landing.nav.openMenu")}
            className="w-10 h-10 rounded-[10px] flex items-center justify-center bg-menubtnbg shadow-[0px_1px_1.5px_0.1px_#16192408]"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.49683 4.99792H17.5031"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M14.1684 10H2.49683"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M2.49683 15.0021H10.8336"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay and Slide */}
      <div
        className={`fixed inset-0 lg:hidden transition-opacity duration-300 ${
          isMenuOpen
            ? "opacity-100 bg-black/50 pointer-events-auto"
            : "opacity-0 bg-transparent pointer-events-none"
        }`}
        onClick={toggleMenu}
      ></div>

      {/* bg-landingPageBG always, remove conditional so it's not only on lg */}
      <div
        className={`fixed top-0 right-0 h-screen w-[80%] max-w-sm bg-landingPageBG shadow-lg lg:hidden transform transition-transform duration-300 ease-in-out z-30 ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        } overflow-y-auto`}
      >
        <div className="p-5">
          <button
            onClick={toggleMenu}
            aria-label={t("landing.nav.closeMenu")}
            className="ml-auto w-10 h-10 rounded-[10px] flex items-center justify-center bg-menubtnbg shadow-[0px_1px_1.5px_0.1px_#16192408]"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <nav className="mt-4 flex flex-col gap-8">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="text-white text-[16px] leading-6 font-medium"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-gray-600"></div>
            <LanguageSwitch mobile />
            {user ? (
              <>
                <button className="text-white text-[16px] leading-6 font-medium text-left" onClick={() => navigate("/userProfile")}>
                  {t("landing.nav.profile")}
                </button>
                <button className="text-white text-[16px] leading-6 font-medium text-left" onClick={handleLogout}>
                  {t("landing.nav.logout")}
                </button>
              </>
            ) : (
              <button className="text-white text-[16px] leading-6 font-medium text-left" onClick={() => navigate("/login")}>
                {t("landing.nav.login")}
              </button>
            )}
            <button
              onClick={user ? (isActive ? () => navigate("/dashboard") : () => navigate("/payment")) : openBookDemo}
              className="bg-white h-11 rounded-lg px-4 text-primary text-[16px] leading-6 font-semibold shadow-[0px_0px_0px_1px_#01040B,_0px_1px_2px_0px_#01040B7A] w-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(15,23,42,0.2)] active:translate-y-0 active:shadow-[0px_0px_0px_1px_#01040B,_0px_1px_2px_0px_#01040B7A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              {user ? (isActive ? t("landing.nav.toDashboard") : t("landing.nav.subscribe")) : t("landing.nav.getStarted")}
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};
